import { type FC, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
import ExclamationCircle from 'dibs-icons/exports/legacy/ExclamationCircle';
import classnames from 'classnames';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { useConnectedBalance } from 'dibs-crypto-utils/exports/useConnectedBalance';
import { shortAddress, shortName } from 'dibs-crypto-utils/exports/formattingUtils';
import { NftAvatarCircle } from 'dibs-crypto-utils/exports/NftAvatarCircle';

import { ConnectedWalletMenuContent } from './ConnectedWalletMenuContent';
import { CreateNftButton } from './CreateNftButton';
import { type ConnectedWalletRespMenu_user$key } from './__generated__/ConnectedWalletRespMenu_user.graphql';
import styles from './styles/ConnectedWalletStatus.scss';

const messages = defineMessages({
    yourConnectedAccount: {
        id: 'connectMetamask.connectedAccount',
        defaultMessage: 'Your connected account',
    },
});

type Props = {
    user: ConnectedWalletRespMenu_user$key;
    hideCreate: boolean;
};

export const ConnectedWalletRespMenu: FC<Props> = ({ user: userRef, hideCreate }) => {
    const { formatMessage } = useIntl();
    const { formattedBalance } = useConnectedBalance();
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const { account: connectedAddress, unsupportedChain } = useWeb3Connection();

    const user = useFragment(
        graphql`
            fragment ConnectedWalletRespMenu_user on User {
                ...ConnectedWalletMenuContent_user
                ethereumWalletAddress {
                    ...NftAvatarCircle_address
                    address
                    ensDomain
                    publicProfile {
                        username
                    }
                }
            }
        `,
        userRef
    );

    const { ethereumWalletAddress } = user;
    const { address: persistedAddress, ensDomain, publicProfile } = ethereumWalletAddress || {};
    const { username } = publicProfile || {};

    if (!persistedAddress) {
        return null;
    }

    const mismatchedAccounts =
        connectedAddress && connectedAddress.toLowerCase() !== persistedAddress.toLowerCase();
    const hasError = mismatchedAccounts || unsupportedChain;

    const buttonClass = classnames(styles.balanceButton, {
        [styles.isTooltipVisible]: isTooltipVisible,
        [styles.hideCreate]: hideCreate,
    });

    return (
        <div className={styles.respMenuWrapper}>
            {/* eslint-disable-next-line react/forbid-elements */}
            <div
                className={buttonClass}
                onMouseEnter={() => setIsTooltipVisible(true)}
                onMouseLeave={() => setIsTooltipVisible(false)}
                aria-label={formatMessage(messages.yourConnectedAccount)}
            >
                {hasError ? <ExclamationCircle className={styles.exclamationIcon} /> : null}
                <NftAvatarCircle
                    address={ethereumWalletAddress}
                    wrapperClassName={styles.desktopMargin}
                />
                <div className={styles.balanceContent}>
                    <p className={styles.account}>
                        {(username && shortName(`@${username}`)) ||
                            (ensDomain && shortName(ensDomain)) ||
                            shortAddress(persistedAddress)}
                    </p>
                    {formattedBalance && <p className={styles.balance}>{formattedBalance}</p>}
                </div>
                <Tooltip
                    isVisible={isTooltipVisible}
                    align="left"
                    paddingSize="none"
                    direction="bottom"
                    onClose={() => setIsTooltipVisible(false)}
                    keepInViewport
                    triangleFillColor="sassyColorMoonstone"
                    zIndex="high"
                >
                    <div className={styles.tooltipContent}>
                        <ConnectedWalletMenuContent user={user} />
                    </div>
                </Tooltip>
            </div>
            {!hideCreate && (
                <CreateNftButton
                    persistedAddress={persistedAddress}
                    buttonSize="small"
                    hasProfile={!!publicProfile}
                />
            )}
        </div>
    );
};
